import {
  collection,
  doc,
  getDoc,
  getFirestore,
  setDoc,
  Timestamp,
  type CollectionReference,
  type DocumentData,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { httpsCallable } from 'firebase/functions';
import type { RackV2, Room, State } from './types';
import { errorMessage } from '../assets/js/helpers/FirebaseErrors';
import { getFbFunctions } from './development';

export type SnapshotKind = 'rack' | 'room';

export interface PitrSnapshotEntry {
  savedAt: Timestamp;
  readTime: Timestamp;
  path?: string;
}

export interface PitrSnapshotListEntry extends PitrSnapshotEntry {
  available: boolean;
  restoreTime: Timestamp;
  retentionDays: number;
  exactTimestamp: boolean;
  source: 'pitr' | 'gcs';
}

const BASIC_RETENTION_DAYS = 7;
const PLUS_RETENTION_DAYS = 30;
const PITR_METADATA_COLLECTION = 'snapshots';
const PITR_METADATA_DOC = 'pitr';
const GCS_METADATA_DOC = 'gcs';
const PITR_DOCUMENT_FUNCTION = 'snapshots-getPitrDocument';

function collectionName(kind: SnapshotKind) {
  return kind === 'rack' ? 'racks' : 'rooms';
}

function baseCollection(context: State, kind: SnapshotKind): CollectionReference<DocumentData, DocumentData> | null {
  const fbApp = getApp(context.firebaseApp);
  const db = getFirestore(fbApp, context.dbid);
  const collectionId = collectionName(kind);

  if (context.team && context.userMode === 'team') {
    return collection(db, 'teams', context.team, collectionId);
  } else if (context.user) {
    return collection(db, 'users', context.user.uid, collectionId);
  }

  return null;
}

function snapshotMetadataDoc(context: State, kind: SnapshotKind, id: string, snapshotDocId: string) {
  const collectionRef = baseCollection(context, kind);
  if (!collectionRef) {
    throw new Error('Error retrieving snapshot metadata from database.');
  }

  return doc(collectionRef, id, PITR_METADATA_COLLECTION, snapshotDocId);
}

async function fetchSnapshotEntries(context: State, kind: SnapshotKind, id: string, snapshotDocId: string) {
  const metadataDoc = snapshotMetadataDoc(context, kind, id, snapshotDocId);
  const snap = await getDoc(metadataDoc);

  return ((snap.data()?.timestamps || [])
    .map(normalizeSnapshotEntry)
    .filter(Boolean) as PitrSnapshotEntry[]);
}

export function roundUpToPitrMinute(date: Date) {
  const rounded = new Date(date);
  const hasPartialMinute = rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0;
  rounded.setSeconds(0, 0);
  if (hasPartialMinute) {
    rounded.setMinutes(rounded.getMinutes() + 1);
  }
  return rounded;
}

export function createPitrSnapshotEntry(date = new Date()): PitrSnapshotEntry {
  return {
    savedAt: Timestamp.fromDate(date),
    readTime: Timestamp.fromDate(roundUpToPitrMinute(date)),
  };
}

export function timestampMillis(value: any): number {
  if (!value) return 0;
  if (value instanceof Timestamp) return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'object') {
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.seconds === 'number') {
      return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1000000);
    }
    if (typeof value._seconds === 'number') {
      return value._seconds * 1000 + Math.floor((value._nanoseconds || 0) / 1000000);
    }
  }
  return 0;
}

export function snapshotSettingsForSubscription(subscription: string) {
  const plan = subscription.toLowerCase();
  const exactTimestamp = plan === 'plus';

  return {
    exactTimestamp,
    retentionDays: exactTimestamp ? PLUS_RETENTION_DAYS : BASIC_RETENTION_DAYS,
    snapshotDocId: exactTimestamp ? GCS_METADATA_DOC : PITR_METADATA_DOC,
  };
}

export function prunePitrSnapshotEntries(
  entries: PitrSnapshotEntry[],
  now = new Date(),
  retentionDays = PLUS_RETENTION_DAYS,
  dedupeBy: 'savedAt' | 'readTime' | 'path' = 'savedAt',
) {
  const retentionStart = now.getTime() - retentionDays * 24 * 60 * 60 * 1000;
  const bySnapshotKey = new Map<number | string, PitrSnapshotEntry>();

  entries.forEach((entry) => {
    const retentionTime = timestampMillis(entry.savedAt);
    if (retentionTime < retentionStart) return;

    const snapshotKey = dedupeBy === 'path'
      ? entry.path || timestampMillis(entry.savedAt)
      : timestampMillis(entry[dedupeBy]);
    const existing = bySnapshotKey.get(snapshotKey);
    if (!existing || timestampMillis(entry.savedAt) > timestampMillis(existing.savedAt)) {
      bySnapshotKey.set(snapshotKey, entry);
    }
  });

  return [...bySnapshotKey.values()].sort((a, b) => timestampMillis(b.savedAt) - timestampMillis(a.savedAt));
}

export function mergePlusSnapshotEntries(
  gcsEntries: PitrSnapshotEntry[],
  pitrEntries: PitrSnapshotEntry[],
  now = new Date(),
) {
  const gcs = prunePitrSnapshotEntries(gcsEntries, now, PLUS_RETENTION_DAYS, 'path');
  const pitr = prunePitrSnapshotEntries(pitrEntries, now, BASIC_RETENTION_DAYS, 'readTime');

  if (gcs.length === 0) {
    return pitr.map((entry) => ({ ...entry, source: 'pitr' as const }));
  }

  const oldestGcsSavedAt = Math.min(...gcs.map((entry) => timestampMillis(entry.savedAt)));
  const olderPitr = pitr.filter((entry) => timestampMillis(entry.savedAt) < oldestGcsSavedAt);

  return [
    ...gcs.map((entry) => ({ ...entry, source: 'gcs' as const })),
    ...olderPitr.map((entry) => ({ ...entry, source: 'pitr' as const })),
  ].sort((a, b) => timestampMillis(b.savedAt) - timestampMillis(a.savedAt));
}

function toSnapshotListEntry(
  entry: PitrSnapshotEntry & { source: 'pitr' | 'gcs' },
  settings: ReturnType<typeof snapshotSettingsForSubscription>,
  now: Date,
): PitrSnapshotListEntry {
  return {
    ...entry,
    retentionDays: settings.retentionDays,
    exactTimestamp: entry.source === 'gcs',
    restoreTime: entry.readTime,
    available: entry.source === 'gcs' ? true : timestampMillis(entry.readTime) <= now.getTime(),
  };
}

function normalizeSnapshotEntry(entry: any): PitrSnapshotEntry | null {
  const savedAtMillis = timestampMillis(entry?.savedAt);
  const readTimeMillis = timestampMillis(entry?.readTime ?? entry?.timestamp);
  if (!savedAtMillis || !readTimeMillis) return null;

  return {
    savedAt: Timestamp.fromMillis(savedAtMillis),
    readTime: Timestamp.fromMillis(readTimeMillis),
    path: typeof entry?.path === 'string' ? entry.path : undefined,
  };
}

export function reviveCallableTimestamps(value: any): any {
  if (Array.isArray(value)) {
    return value.map(reviveCallableTimestamps);
  }

  if (value instanceof Timestamp || value instanceof Date) {
    return value;
  }

  if (value && typeof value === 'object') {
    if (typeof value.toMillis === 'function') {
      return value;
    }

    if (typeof value.seconds === 'number') {
      return new Timestamp(value.seconds, value.nanoseconds || 0);
    }

    if (typeof value._seconds === 'number') {
      return new Timestamp(value._seconds, value._nanoseconds || 0);
    }

    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, reviveCallableTimestamps(item)]),
    );
  }

  return value;
}

async function staleReadDocument(
  context: State,
  kind: SnapshotKind,
  id: string,
  snapshot: { readTime?: Timestamp; path?: string },
) {
  if (!snapshot.path && !snapshot.readTime) {
    throw new Error('Snapshot read time or path is required.');
  }

  const fbApp = getApp(context.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const getPitrDocument = httpsCallable(functions, PITR_DOCUMENT_FUNCTION);
  const result = await getPitrDocument({
    kind,
    id,
    ...(snapshot.readTime ? { readTime: snapshot.readTime.toDate().toISOString() } : {}),
    ...(snapshot.path ? { path: snapshot.path } : {}),
    dbid: context.dbid,
    team: context.team,
    userMode: context.userMode,
  });
  const data = result.data as { document?: RackV2 | Room } | RackV2 | Room | null;
  const payload = data as { document?: RackV2 | Room } | null;
  const restored = reviveCallableTimestamps(payload?.document ?? data);

  if (!restored) {
    throw new Error('Snapshot not found at the selected time.');
  }

  return { ...restored, id };
}

export async function fetchPitrSnapshots({
  kind,
  id,
}: {
  kind: SnapshotKind;
  id: string;
}): Promise<PitrSnapshotListEntry[]> {
  try {
    const entitlements = await this.getEffectiveEntitlements();
    const settings = snapshotSettingsForSubscription(entitlements.plan);
    const now = new Date();

    if (settings.snapshotDocId === GCS_METADATA_DOC) {
      const [gcsEntries, pitrEntries] = await Promise.all([
        fetchSnapshotEntries(this, kind, id, GCS_METADATA_DOC),
        fetchSnapshotEntries(this, kind, id, PITR_METADATA_DOC),
      ]);

      return mergePlusSnapshotEntries(gcsEntries, pitrEntries, now)
        .map((entry) => toSnapshotListEntry(entry, settings, now));
    }

    const pitrEntries = await fetchSnapshotEntries(this, kind, id, PITR_METADATA_DOC);

    return prunePitrSnapshotEntries(
      pitrEntries,
      now,
      settings.retentionDays,
      'readTime',
    )
      .map((entry) => ({ ...entry, source: 'pitr' as const }))
      .map((entry) => toSnapshotListEntry(entry, settings, now));
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function previewPitrSnapshot({ kind, id, readTime, path }: {
  kind: SnapshotKind;
  id: string;
  readTime?: Timestamp;
  path?: string;
}): Promise<RackV2 | Room> {
  try {
    return await staleReadDocument(this, kind, id, { readTime, path }) as RackV2 | Room;
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function restorePitrSnapshot({ kind, id, readTime, path }: {
  kind: SnapshotKind;
  id: string;
  readTime?: Timestamp;
  path?: string;
}): Promise<RackV2 | Room> {
  const entitlements = await this.getEffectiveEntitlements();
  if (!['basic', 'plus'].includes(entitlements.plan.toLowerCase())) {
    throw new Error('Snapshots are available on Basic and Plus plans.');
  }

  try {
    const restored = await staleReadDocument(this, kind, id, { readTime, path });

    if (kind === 'rack') {
      await this.updateRack(restored as RackV2);
    } else {
      await this.updateRoom(restored as Room);
    }

    return restored as RackV2 | Room;
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function clearPitrSnapshotMetadata(context: State, kind: SnapshotKind, id: string) {
  const metadataDoc = snapshotMetadataDoc(context, kind, id, PITR_METADATA_DOC);
  await setDoc(metadataDoc, {
    timestamps: [],
    updatedAt: Timestamp.fromDate(new Date()),
  }, { merge: true });
}
